import React, { useState, useRef, useCallback, useEffect } from 'react';
import infoIcon from '../../assets/info-deep-blue.svg';
import './IconTooltip.css';

export const TOOL_TIP_POSITION = {
  center: {
    containerArrowPosition: 'Icon-tooltip-arrow-container-center',
    left: -115,
  },
  end: {
    containerArrowPosition: 'Icon-tooltip-arrow-container-end',
    left: -204,
  },
  start: {
    containerArrowPosition: 'Icon-tooltip-arrow-container-start',
    left: -24,
  },
};

const IconTooltip = ({
  backgroundColor = 'var(--blue-main)',
  borderColor = '#000',
  containerClassName,
  description,
  descriptionClassName,
  icon,
  position = TOOL_TIP_POSITION.center,
  name = '',
}) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const iconRef = useRef();
  const tooltipRef = useRef();
  const id = `tooltip-${name.replace(' ', '-')}`;
  const [tooltipHeight, setTooltipHeight] = useState(0);

  useEffect(() => {
    if (tooltipVisible && tooltipRef.current && tooltipHeight === 0) {
      setTooltipHeight(tooltipRef.current.getBoundingClientRect().height);
    }
  }, [tooltipVisible, tooltipHeight]);

  const containerLocation = () => {
    if (!iconRef.current) {
      return {};
    }

    const { offsetTop, offsetLeft, height: iconHeight } = iconRef.current;

    const top = offsetTop - (tooltipHeight + iconHeight / 2);
    const left = offsetLeft + position.left;
    return { left: `${left}px`, top: `${top}px` };
  };

  const handleHoverIn = useCallback(() => {
    setTooltipVisible(true);
  }, []);

  const handleHoverOut = useCallback(() => {
    setTooltipVisible(false);
  }, []);

  const handleVisibility = useCallback(() => {
    if (tooltipVisible) {
      handleHoverOut();
    } else {
      handleHoverIn();
    }
  }, [handleHoverIn, handleHoverOut, tooltipVisible]);

  function renderTooltip() {
    if (!tooltipVisible) {
      return null;
    }

    return (
      <div
        className="Icon-tooltip-main-container"
        id={id}
        role="tooltip"
        style={containerLocation()}
      >
        <div
          ref={tooltipRef}
          className={'Icon-tooltip-description-container '
            + `${containerClassName}`}
          style={{
            backgroundColor,
            borderColor,
            borderStyle: 'solid',
            borderWidth: 1,
          }}
        >
          <span className={`Icon-tooltip-description ${descriptionClassName}`}>
            {description}
          </span>
        </div>

        <div className={'Icon-tooltip-arrow-container '
          + `${position.containerArrowPosition}`}
        >
          <div
            className="Icon-tooltip-arrow-down-border"
            style={{
              borderTopColor: `${borderColor || backgroundColor}`,
              borderTopStyle: 'solid',
              borderTopWidth: '10px',
            }}
          />

          <div
            className="Icon-tooltip-arrow-down"
            style={{ borderTop: `10px solid ${backgroundColor}` }}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      {renderTooltip()}

      <button
        aria-describedby={id}
        aria-label={tooltipVisible ? description : ''}
        aria-live={tooltipVisible ? 'assertive' : null}
        className="Icon-tooltip-icon-button"
        onBlur={handleHoverOut}
        onClick={handleVisibility}
        type="button"
      >
        <img
          ref={iconRef}
          alt="info"
          className="Icon-tooltip-icon-default"
          onMouseEnter={handleHoverIn}
          onMouseLeave={handleHoverOut}
          src={icon || infoIcon}
        />
      </button>
    </>
  );
};

export default IconTooltip;
